import { useQuery } from '@tanstack/react-query';
import { getSurveyReport, type SurveyPayload } from './report';
import { previewPayload, PREVIEW_AS_OF, SU_EMPTY, SU_PREVIEW } from './fixture';

/*
 * Fetches the Système U quiz report. When the call fails we fall back:
 *   - dev  → the design's figures, so the page can be reviewed as the real thing
 *   - prod → zeros and "—", so a deployed build never shows invented figures
 * Either way `isPreview` is true and the header shows a "No live data" pill.
 *
 * DEV ONLY: `?preview` shows the design's figures even when the report loads
 * (to review a layout the live payload cannot fill yet), and
 * `?preview=2026-10-28` shows them as they stand on that campaign day.
 */
export function useSurveyReport(reportId: string, baseUrl: string): { data: SurveyPayload; isPreview: boolean; lastRefreshedAt: string | null } {
  const forced = import.meta.env.DEV ? devPreviewDate() : null;
  const query = useQuery({
    queryKey: ['survey-report', baseUrl, reportId],
    queryFn: () => getSurveyReport(reportId, baseUrl),
    // A forced preview never shows the report, so it does not ask for it.
    enabled: !forced,
    retry: false,
    // The pipeline refreshes the payload on a schedule; poll so an open page
    // keeps up with it, like the other real-time dashboards.
    refetchInterval: 5 * 60_000,
  });

  if (forced) return { data: forced === PREVIEW_AS_OF ? SU_PREVIEW : previewPayload(forced), isPreview: true, lastRefreshedAt: null };

  if (query.data) {
    return { data: query.data.data, isPreview: false, lastRefreshedAt: query.data.meta?.last_refreshed_at ?? null };
  }
  return { data: import.meta.env.DEV ? SU_PREVIEW : SU_EMPTY, isPreview: true, lastRefreshedAt: null };
}

/** `?preview` → the design's default day; `?preview=YYYY-MM-DD` → that day; absent → null. */
function devPreviewDate(): string | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('preview');
  if (value == null) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : PREVIEW_AS_OF;
}
